import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useCalculatorModal } from "../../context/CalculatorContext";
import ImageSelect from "./ImageSelect";
import request from "../../request";
import "./calculatormodal.css";

// const CALCULATOR_URL = "https://api2.giftcard8.com/gc/card/calculatorNew";
const CALCULATOR_URL = "https://test-giftcard8-api.gcard8.com/gc/card/calculatorNew";
const COUNTRY_LIST_URL = "https://test-giftcard8-api.gcard8.com/gc/public/countrylist";
// const COUNTRY_LIST_URL = "https://api2.giftcard8.com/gc/public/countrylist";

// ─── Helper function to check if face value amount falls in card's range ──────
const amountInCard = (item, amount) => {
  if (!amount || amount <= 0) return false;
  if (item.facevalue_fixed && item.facevalue_fixed.trim() !== "") {
    const fixed = item.facevalue_fixed.split(",").map(Number);
    return fixed.includes(amount);
  }
  const min = item.facevalue_min ?? 0;
  const max = item.facevalue_max ?? 0;
  if (min === 0 && max === 0) return false;
  return amount >= min && amount <= max;
};

const CalculatorModal = () => {
  const { isOpen, preselectedData, closeCalculator } = useCalculatorModal();

  // === Data state ===
  const [residenceCountries, setResidenceCountries] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);

  // === Selection state — start empty so placeholders show by default ===
  const [residenceCountryId, setResidenceCountryId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [currency, setCurrency] = useState("");
  const [isPhysical, setIsPhysical] = useState(null);
  const [speed, setSpeed] = useState(null);
  const [faceValue, setFaceValue] = useState("");
  const [debouncedFaceValue, setDebouncedFaceValue] = useState("");

  // === UI state ===
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCards, setLoadingCards] = useState(false);
  const [fetchError, setFetchError] = useState("");

  // ─── Debounce faceValue input ────────────────────────────────────────────────
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFaceValue(faceValue);
    }, 500);
    return () => clearTimeout(handler);
  }, [faceValue]);

  // ─── Fetch residence countries once on open ──────────────────────────────────
  useEffect(() => {
    if (!isOpen || residenceCountries.length > 0) return;

    setLoadingCountries(true);
    setFetchError("");

    request(COUNTRY_LIST_URL, "POST", {})
      .then((data) => {
        const list = Array.isArray(data?.data) ? data.data : [];
        setResidenceCountries(list);
      })
      .catch((err) => {
        console.error("Country list fetch failed:", err);
        setFetchError("Could not load countries. Please try again.");
      })
      .finally(() => setLoadingCountries(false));
  }, [isOpen, residenceCountries.length]);

  // ─── Base catalog fetch when modal opens or residence country changes ───────
  useEffect(() => {
    if (!isOpen) return;

    const countryToUse = residenceCountryId || "14";

    setLoadingCards(true);
    setFetchError("");

    const fetchBaseCategories = async () => {
      try {
        const data = await request(CALCULATOR_URL, "POST", {
          app_version: "2.6.2",
          country_id: countryToUse,
        });
        const list = data?.data?.card_list || [];
        if (list.length > 0) {
          setAllCategories(list);
        } else {
          setFetchError(data?.msg || "No card data returned. Please try again.");
        }
      } catch (err) {
        console.error("[CalculatorModal] fetchBaseCategories failed:", err);
        setFetchError("Could not load gift card rates. Please try again.");
      } finally {
        setLoadingCards(false);
      }
    };

    fetchBaseCategories();
  }, [isOpen, residenceCountryId]);

  // ─── Filter fetch when ALL parameters (country_id, category_id, country, balance) are set ───
  useEffect(() => {
    if (!isOpen) return;

    const countryToUse = residenceCountryId || "14";
    const amountNum = parseFloat(debouncedFaceValue);
    const hasValidAmount = !isNaN(amountNum) && amountNum > 0;

    // Call endpoint ONLY after ALL required parameters are provided
    if (!categoryId || !currency || !hasValidAmount) {
      setFilteredCards([]);
      return;
    }

    const payload = {
      app_version: "2.6.2",
      country_id: countryToUse,
      category_id: categoryId,
      country: currency,
      balance: amountNum,
    };

    setLoadingCards(true);

    const fetchFilteredCards = async () => {
      try {
        const data = await request(CALCULATOR_URL, "POST", payload);
        const cardList = data?.data?.card_list || [];
        if (cardList.length > 0) {
          const matchedCat =
            cardList.find((c) => String(c.category_id) === String(categoryId)) ||
            cardList[0];
          setFilteredCards(matchedCat?.list || []);
        } else {
          setFilteredCards([]);
        }
      } catch (err) {
        console.error("[CalculatorModal] fetchFilteredCards failed:", err);
      } finally {
        setLoadingCards(false);
      }
    };

    fetchFilteredCards();
  }, [isOpen, residenceCountryId, categoryId, currency, debouncedFaceValue]);

  // ─── Reset selections when modal opens/closes or preselectedData changes ────
  useEffect(() => {
    if (isOpen) {
      if (preselectedData) {
        if (preselectedData.category_id) {
          setCategoryId(String(preselectedData.category_id));
        } else if (preselectedData.card_name && allCategories.length > 0) {
          const found = allCategories.find(
            (cat) =>
              cat.category_name.toLowerCase().includes(preselectedData.card_name.toLowerCase().trim()) ||
              preselectedData.card_name.toLowerCase().includes(cat.category_name.toLowerCase().trim())
          );
          if (found) {
            setCategoryId(String(found.category_id));
          }
        }
        if (preselectedData.currency) {
          setCurrency(preselectedData.currency);
        }
      }
    } else {
      setResidenceCountryId("");
      setCategoryId("");
      setCurrency("");
      setIsPhysical(null);
      setSpeed(null);
      setFaceValue("");
      setFilteredCards([]);
      setFetchError("");
    }
  }, [isOpen, preselectedData, allCategories]);

  // ─── Current selected category object (from full catalog) ───────────────────
  const currentCategory = useMemo(() => {
    if (!allCategories.length || !categoryId) return null;
    return (
      allCategories.find((c) => String(c.category_id) === String(categoryId)) || null
    );
  }, [allCategories, categoryId]);

  // ─── Combined card items (prefer server-filtered list if available) ──────────
  const cardItems = useMemo(() => {
    if (filteredCards && filteredCards.length > 0) {
      return filteredCards;
    }
    return currentCategory?.list || [];
  }, [filteredCards, currentCategory]);

  // ─── Available country/currency options for selected card ────────────────────
  const availableCurrencies = useMemo(() => {
    if (!currentCategory) return [];
    if (currentCategory.country_options?.length > 0) {
      return currentCategory.country_options;
    }
    const seen = new Map();
    (currentCategory.list || []).forEach((item) => {
      if (item.currency && !seen.has(item.currency)) {
        seen.set(item.currency, {
          currency: item.currency,
          country_name: item.currency,
          short_name: item.currency,
          flag: null,
          image_url: null,
        });
      }
    });
    return Array.from(seen.values());
  }, [currentCategory]);

  // ─── Physical card type options (strictly filtered by selected card country) ──
  const availablePhysicalOptions = useMemo(() => {
    if (!currentCategory || !currency) return [];

    let items = (cardItems.length > 0 ? cardItems : currentCategory.list || []).filter(
      (item) => item.currency === currency
    );
    if (items.length === 0) return [];

    const amount = parseFloat(faceValue);
    if (amount > 0) {
      const amountMatching = items.filter((item) => amountInCard(item, amount));
      if (amountMatching.length > 0) {
        items = amountMatching;
      }
    }

    const seen = new Map();
    items.forEach((item) => {
      if (
        item.is_physical !== undefined &&
        item.is_physical !== null &&
        Number(item.is_physical) !== 0
      ) {
        const text = item.is_physical_text || String(item.is_physical);
        if (!seen.has(item.is_physical)) {
          seen.set(item.is_physical, text);
        }
      }
    });

    return Array.from(seen.entries()).map(([value, text]) => ({ value, text }));
  }, [currentCategory, currency, faceValue, cardItems]);

  // ─── Speed options (strictly filtered by selected card country) ───────────────
  const availableSpeedOptions = useMemo(() => {
    if (!currentCategory || !currency) return [];

    let items = (cardItems.length > 0 ? cardItems : currentCategory.list || []).filter(
      (item) => item.currency === currency
    );
    if (items.length === 0) return [];

    const amount = parseFloat(faceValue);
    if (amount > 0) {
      const amountMatching = items.filter((item) => amountInCard(item, amount));
      if (amountMatching.length > 0) {
        items = amountMatching;
      }
    }

    const seen = new Map();
    items.forEach((item) => {
      if (
        item.speed !== undefined &&
        item.speed !== null &&
        Number(item.speed) !== 0
      ) {
        const text = item.speed_text || String(item.speed);
        if (!seen.has(item.speed)) {
          seen.set(item.speed, text);
        }
      }
    });

    return Array.from(seen.entries()).map(([value, text]) => ({ value, text }));
  }, [currentCategory, currency, faceValue, cardItems]);

  // ─── Auto-select first physical type option by default / when filtered ─────────
  useEffect(() => {
    if (availablePhysicalOptions.length > 0) {
      const exists = availablePhysicalOptions.some(
        (opt) => Number(opt.value) === Number(isPhysical)
      );
      if (!exists || isPhysical === null) {
        setIsPhysical(availablePhysicalOptions[0].value);
      }
    } else {
      setIsPhysical(null);
    }
  }, [availablePhysicalOptions, isPhysical]);

  // ─── Auto-select first speed option by default ──────────────────────────────
  useEffect(() => {
    if (availableSpeedOptions.length > 0) {
      const exists = availableSpeedOptions.some(
        (opt) => Number(opt.value) === Number(speed)
      );
      if (!exists || speed === null) {
        setSpeed(availableSpeedOptions[0].value);
      }
    } else {
      setSpeed(null);
    }
  }, [availableSpeedOptions, speed]);

  // ─── Handle category change: reset currency so user selects it manually ───────
  const handleCategoryChange = useCallback((val) => {
    setCategoryId(val);
    setCurrency("");
    setIsPhysical(null);
    setSpeed(null);
    setFaceValue("");
    setFilteredCards([]);
  }, []);

  const handleCurrencyChange = useCallback((val) => {
    setCurrency(val);
    setIsPhysical(null);
    setSpeed(null);
    setFaceValue("");
    setFilteredCards([]);
  }, []);

  const handleResidenceChange = useCallback((val) => {
    setResidenceCountryId(val);
    setCategoryId("");
    setCurrency("");
    setIsPhysical(null);
    setSpeed(null);
    setFaceValue("");
    setFilteredCards([]);
  }, []);

  // ─── Find best matching card item ────────────────────────────────────────────
  const matchedCard = useMemo(() => {
    if (!cardItems.length || !currency) return null;
    const amount = parseFloat(faceValue);
    if (!amount || amount <= 0) return null;

    const currencyItems = cardItems.filter((item) => item.currency === currency);
    if (currencyItems.length === 0) return null;

    const amountMatching = currencyItems.filter((item) =>
      amountInCard(item, amount)
    );

    // Amount is out of range — show 0 rate, do NOT fall back to all currency items
    if (amountMatching.length === 0) return null;

    let candidates = amountMatching;

    // Filter by physical type pill if user selected one
    if (isPhysical !== null && isPhysical !== undefined) {
      const typeMatching = candidates.filter(
        (item) =>
          Number(item.is_physical) === Number(isPhysical) ||
          Number(item.is_physical) === 5
      );
      if (typeMatching.length > 0) {
        candidates = typeMatching;
      }
    }

    // Filter by speed pill if user selected one
    if (speed !== null && speed !== undefined && Number(speed) !== 0) {
      const speedMatching = candidates.filter(
        (item) => Number(item.speed) === Number(speed)
      );
      if (speedMatching.length > 0) {
        candidates = speedMatching;
      }
    }

    // Pick the card with the lowest facevalue_min (widest/most general range)
    // This ensures the base rate is shown rather than a more restrictive higher-rate card
    return candidates.reduce(
      (best, cur) => (cur.facevalue_min < best.facevalue_min ? cur : best),
      candidates[0]
    );
  }, [cardItems, currency, isPhysical, speed, faceValue]);

  // ─── Residence country local currency symbol ──────────────────────────────────
  const residenceCurrencySymbol = useMemo(() => {
    if (!residenceCountries.length || !residenceCountryId) return "₦";
    const country = residenceCountries.find(
      (c) => String(c.id) === String(residenceCountryId)
    );
    return (
      country?.currency_symbol ||
      country?.symbol ||
      country?.local_currency_symbol ||
      country?.currencySymbol ||
      "₦"
    );
  }, [residenceCountries, residenceCountryId]);

  // ─── Payout & Rate calculation ───────────────────────────────────────────────
  const hasEnteredAmount = useMemo(() => {
    const amount = parseFloat(faceValue);
    return !isNaN(amount) && amount > 0;
  }, [faceValue]);

  const { estimatedPayout, rateLabel } = useMemo(() => {
    const sym = residenceCurrencySymbol;

    if (!hasEnteredAmount) {
      return { estimatedPayout: 0, rateLabel: null };
    }

    // Always use the selected card currency — matchedCard may be null when out of range
    const cardSym = currency || "USD";

    if (!matchedCard) {
      // Out of range (amount entered doesn't match any card min/max)
      return {
        estimatedPayout: 0,
        rateLabel: `${sym}0/${cardSym}`,
      };
    }

    const rate = Number(matchedCard.rate || 0);
    const amount = parseFloat(faceValue) || 0;
    const payout = amount * rate;
    const rateFormatted = rate.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return {
      estimatedPayout: payout,
      rateLabel: `${sym}${rateFormatted}/${cardSym}`,
    };
  }, [hasEnteredAmount, matchedCard, faceValue, residenceCurrencySymbol, currency]);

  const payoutFormatted = estimatedPayout.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // ─── Face value helper ────────────────────────────────────────────────────────
  const faceValueHelper = useMemo(() => {
    if (!matchedCard) return "";
    if (matchedCard.facevalue_fixed) {
      const vals = matchedCard.facevalue_fixed.split(",");
      return `Fixed amounts: ${vals.slice(0, 6).join(", ")}${vals.length > 6 ? "…" : ""}`;
    }
    if (matchedCard.facevalue_min || matchedCard.facevalue_max) {
      return `Range: ${matchedCard.facevalue_min || 1} – ${matchedCard.facevalue_max || "∞"}`;
    }
    return "";
  }, [matchedCard]);

  // ─── Build ImageSelect option arrays ─────────────────────────────────────────
  const residenceOptions = useMemo(
    () =>
      residenceCountries.map((c) => ({
        value: String(c.id),
        label: c.name,
        image: c.image || null,
      })),
    [residenceCountries]
  );

  const categoryOptions = useMemo(
    () =>
      allCategories.map((c) => ({
        value: String(c.category_id),
        label: c.category_name,
        image: c.category_image || null,
      })),
    [allCategories]
  );

  const currencyOptions = useMemo(
    () =>
      availableCurrencies.map((co) => ({
        value: co.currency,
        label: co.country_name || co.short_name || co.currency,
        image: co.flag || co.image_url || null,
      })),
    [availableCurrencies]
  );

  if (!isOpen) return null;

  const loading = loadingCountries || loadingCards;

  return (
    <div
      className="calc-modal-backdrop"
      onClick={closeCalculator}
      role="presentation"
    >
      <div
        className="calc-modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="calculator-modal-title"
      >
        {/* ── Header ── */}
        <div className="calc-modal-header">
          <h3 className="calc-modal-title" id="calculator-modal-title">
            Calculator
          </h3>
          <button
            className="calc-modal-close-btn"
            onClick={closeCalculator}
            aria-label="Close calculator"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* ── Body (scrollable) ── */}
        <div className="calc-modal-body">
          {/* ── Payout Banner ── */}
          <div className="calc-banner-subtitle">Gift Card Estimated Value</div>
          <div className="calc-banner-amount-wrapper">
            <div className="calc-banner">
              <h2 className="calc-banner-amount">
                {residenceCurrencySymbol}
                {payoutFormatted}
              </h2>
            </div>
            {/* Rate is TOTALLY HIDDEN until user enters an amount */}
            {hasEnteredAmount && rateLabel && (
              <div className="calc-banner-footer">
                <span className="calc-banner-rate">Rate: {rateLabel}</span>
              </div>
            )}
          </div>

          {/* ── Error message ── */}
          {fetchError && (
            <div className="calc-error-msg">
              <i className="bi bi-exclamation-circle me-2"></i>
              {fetchError}
            </div>
          )}

          {/* ── Loading spinner ── */}
          {loading && (
            <div className="text-center py-4">
              <div
                className="spinner-border"
                role="status"
                style={{ color: "#0e6878" }}
              >
                <span className="visually-hidden">Loading…</span>
              </div>
              <p className="calc-helper mt-2">
                {loadingCountries
                  ? "Loading countries…"
                  : "Fetching real-time exchange rates…"}
              </p>
            </div>
          )}

          {/* ── Form ── */}
          {!loading && !fetchError && (
            <div className="calc-form">
              {/* Country of Residence */}
              <div className="calc-form-group">
                <label className="calc-label" htmlFor="residence-select">
                  Select Country of Residence
                </label>
                <ImageSelect
                  id="residence-select"
                  options={residenceOptions}
                  value={residenceCountryId}
                  onChange={handleResidenceChange}
                  placeholder="Select country of residence"
                />
              </div>

              {/* Card Type + Card Country row */}
              <div className="calc-form-group calc-grid-2">
                <div>
                  <label className="calc-label" htmlFor="card-type-select">
                    Select Card Type
                  </label>
                  <ImageSelect
                    id="card-type-select"
                    options={categoryOptions}
                    value={categoryId}
                    onChange={handleCategoryChange}
                    placeholder="Select card type"
                  />
                </div>

                <div>
                  <label className="calc-label" htmlFor="card-country-select">
                    Select Card Country
                  </label>
                  <ImageSelect
                    id="card-country-select"
                    options={currencyOptions}
                    value={currency}
                    onChange={handleCurrencyChange}
                    placeholder="Select card country"
                    disabled={currencyOptions.length === 0}
                  />
                </div>
              </div>

              {/* Type pills (Physical / E-Code / Cash Receipt ...) — shown only when BOTH card type & card country are selected */}
              {!!(categoryId && currency && availablePhysicalOptions.length > 0) && (
                <div className="calc-form-group">
                  <label className="calc-label">Type</label>
                  <div className="calc-pills-container">
                    {availablePhysicalOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`calc-pill ${isPhysical !== null &&
                          Number(isPhysical) === Number(opt.value)
                          ? "active"
                          : ""
                          }`}
                        onClick={() => setIsPhysical(opt.value)}
                      >
                        {opt.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Speed pills — shown only when BOTH card type & card country are selected */}
              {!!(categoryId && currency && availableSpeedOptions.length > 0) && (
                <div className="calc-form-group">
                  <label className="calc-label">Speed</label>
                  <div className="calc-pills-container">
                    {availableSpeedOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`calc-pill ${speed !== null && Number(speed) === Number(opt.value)
                          ? "active"
                          : ""
                          }`}
                        onClick={() => setSpeed(opt.value)}
                      >
                        {opt.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Gift Card Amount */}
              <div className="calc-form-group">
                <label className="calc-label" htmlFor="giftcard-amount-input">
                  Gift Card Amount
                </label>
                <input
                  id="giftcard-amount-input"
                  type="text"
                  inputMode="decimal"
                  min="1"
                  className="calc-input"
                  placeholder={
                    matchedCard?.facevalue_min
                      ? `e.g. ${matchedCard.facevalue_min} – ${matchedCard.facevalue_max || "∞"}`
                      : "0 - 100,000,000,000"
                  }
                  value={faceValue}
                  onChange={(e) => {
                    let raw = e.target.value;
                    // Strip any character that is not a digit or a dot
                    raw = raw.replace(/[^0-9.]/g, "");
                    // Ensure only one decimal point is allowed
                    const parts = raw.split(".");
                    if (parts.length > 2) {
                      raw = parts[0] + "." + parts.slice(1).join("");
                    }
                    // Enforce 12-character maximum
                    if (raw.length > 12) return;
                    setFaceValue(raw);
                  }}
                  maxLength={12}
                />
                {faceValueHelper && (
                  <div className="calc-helper">{faceValueHelper}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Floating Footer ── */}
        <div className="calc-modal-footer">
          <button
            className="calc-cta-btn"
            onClick={() => {
              window.location.href = "https://dashboard.giftcard8.com/account";
            }}
          >
            Login to Sell
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalculatorModal;
